import { getNestConnection, requestNestHTTP } from "./client"

export interface NestFileBytesQuery {
    workspaceId?: string
    worktreeId?: string
    path?: string
    hostPath?: string
}

export interface NestGitFileBytesQuery {
    workspaceId: string
    path?: string
    worktreeId?: string
    treeish: string
    filePath: string
}

export type NestGitFileBytesDescription =
    | {
          exists: true
          mediaType: string
          sizeBytes: number
          etag?: string
      }
    | { exists: false }

export interface NestFileBytes {
    blob: Blob
    mediaType: string
    status?: number
    sizeBytes?: number
    byteLength?: number
    contentRange?: string
    etag?: string
    lastModified?: string
}

export interface NestFileBytesRange {
    start: number
    endExclusive: number
}

function fileBytesPath(endpoint: "/v1/files" | "/v1/git/files", query: object): string {
    const url = new URL(`http://nest.invalid${endpoint}`)
    for (const [key, value] of Object.entries(query)) {
        if (value) url.searchParams.set(key, value)
    }
    return `${url.pathname}${url.search}`
}

async function readErrorMessage(response: Response): Promise<string> {
    try {
        const value: unknown = await response.json()
        if (typeof value === "object" && value !== null && "error" in value) {
            const message = (value as { error: unknown }).error
            if (typeof message === "string") return message
        }
    } catch {
        // Fall through to the HTTP status text.
    }
    return response.statusText || `HTTP ${response.status}`
}

async function downloadFileBytes(
    path: string,
    errorPrefix: string,
    options: { signal?: AbortSignal; range?: NestFileBytesRange } = {}
): Promise<NestFileBytes> {
    if (
        options.range &&
        (!Number.isInteger(options.range.start) ||
            !Number.isInteger(options.range.endExclusive) ||
            options.range.start < 0 ||
            options.range.endExclusive <= options.range.start)
    ) {
        throw new Error("Invalid file byte range")
    }
    const connection = await getNestConnection()
    const headers = new Headers({ Authorization: `Bearer ${connection.token}` })
    if (options.range) {
        headers.set("Range", `bytes=${options.range.start}-${options.range.endExclusive - 1}`)
    }
    const response = await requestNestHTTP(connection, path, {
        method: "GET",
        headers,
        ...(options.signal ? { signal: options.signal } : {}),
    })
    if (!response.ok) throw new Error(`${errorPrefix}: ${await readErrorMessage(response)}`)
    const mediaType = response.headers.get("Content-Type") ?? "application/octet-stream"
    const data = await response.arrayBuffer()
    const contentRange = response.headers.get("Content-Range")
    const contentLength = response.headers.get("Content-Length")
    const rangeTotal = contentRange?.match(/\/(\d+)$/)?.[1]
    const sizeBytes = rangeTotal ?? contentLength
    const etag = response.headers.get("ETag")
    const lastModified = response.headers.get("Last-Modified")
    return {
        blob: new Blob([data], { type: mediaType }),
        mediaType,
        status: response.status,
        byteLength: data.byteLength,
        ...(sizeBytes && Number.isFinite(Number(sizeBytes)) ? { sizeBytes: Number(sizeBytes) } : {}),
        ...(contentRange ? { contentRange } : {}),
        ...(etag ? { etag } : {}),
        ...(lastModified ? { lastModified } : {}),
    }
}

/**
 * Streams file bytes over HTTP instead of base64 on the JSON-RPC socket, so
 * previews stay off the notification path and work on every client shell.
 */
export async function downloadNestFileBytes(
    query: NestFileBytesQuery,
    options: { signal?: AbortSignal; range?: NestFileBytesRange } = {}
): Promise<NestFileBytes> {
    return downloadFileBytes(fileBytesPath("/v1/files", query), "File read failed", options)
}

export async function describeNestGitFileBytes(query: NestGitFileBytesQuery, options: { signal?: AbortSignal } = {}): Promise<NestGitFileBytesDescription> {
    const connection = await getNestConnection()
    const response = await requestNestHTTP(connection, fileBytesPath("/v1/git/files", query), {
        method: "HEAD",
        headers: new Headers({ Authorization: `Bearer ${connection.token}` }),
        ...(options.signal ? { signal: options.signal } : {}),
    })
    if (response.status === 404) return { exists: false }
    if (!response.ok) throw new Error(`Git file read failed: ${await readErrorMessage(response)}`)
    const contentLength = response.headers.get("Content-Length")
    const sizeBytes = contentLength === null ? Number.NaN : Number(contentLength)
    if (!Number.isFinite(sizeBytes) || sizeBytes < 0) {
        throw new Error("Git file read failed: missing content length")
    }
    const etag = response.headers.get("ETag")
    return {
        exists: true,
        mediaType: response.headers.get("Content-Type") ?? "application/octet-stream",
        sizeBytes,
        ...(etag ? { etag } : {}),
    }
}

export async function downloadNestGitFileBytes(
    query: NestGitFileBytesQuery,
    options: { signal?: AbortSignal; range?: NestFileBytesRange } = {}
): Promise<NestFileBytes> {
    return downloadFileBytes(fileBytesPath("/v1/git/files", query), "Git file read failed", options)
}
